import React, { useState, lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LiveTradingProvider } from './context/LiveTradingContext';
import { Sidebar } from './components/Sidebar';
import { MobileHeader } from './components/MobileHeader';
import MobileBottomNav from './components/MobileBottomNav';
import QuickActionButton from './components/QuickActionButton';
import SupportChat from './components/SupportChat';
import RealtimeStatsBar from './components/RealtimeStatsBar';
import FrozenOverlay from './components/FrozenOverlay';
import pushService from './services/pushNotificationService';
import wsService from './services/websocketService';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import LandingPage from './pages/LandingPage';
import ArticlePage from './pages/ArticlePage';
import PortalChooserPage from './pages/PortalChooserPage';
import { RecoveryPortalProvider } from './context/RecoveryPortalContext';
import RecoveryLayout from './components/recovery/RecoveryLayout';
import './App.css';

// Lazy load pages for better performance
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const CoinDetailPage = lazy(() => import('./pages/CoinDetailPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const DepositPage = lazy(() => import('./pages/DepositPage'));
const WithdrawPage = lazy(() => import('./pages/WithdrawPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminConsolePage = lazy(() => import('./pages/AdminConsolePage'));
const NewsFeedPage = lazy(() => import('./pages/NewsFeedPage'));
const TradingBotPage = lazy(() => import('./pages/TradingBotPage'));
const RecoveryLoginPage = lazy(() => import('./pages/recovery/RecoveryLoginPage'));
const RecoveryRegisterPage = lazy(() => import('./pages/recovery/RecoveryRegisterPage'));
const RecoveryDashboardPage = lazy(() => import('./pages/recovery/RecoveryDashboardPage'));
const RecoveryDepositPage = lazy(() => import('./pages/recovery/RecoveryDepositPage'));
const RecoveryCasesPage = lazy(() => import('./pages/recovery/RecoveryCasesPage'));
const RecoveryMethodsPage = lazy(() => import('./pages/recovery/RecoveryMethodsPage'));
const RecoveryDocumentsPage = lazy(() => import('./pages/recovery/RecoveryDocumentsPage'));
const RecoveryProfilePage = lazy(() => import('./pages/recovery/RecoveryProfilePage'));
const RecoverySupportPage = lazy(() => import('./pages/recovery/RecoverySupportPage'));

// Loading spinner component
const PageLoader = () => (
    <div className="min-h-[50vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-[#5BA4A4] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-500 text-sm">Loading...</p>
        </div>
    </div>
);

// Protected Route Component (Trading portal)
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-2 border-[#5BA4A4] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-[#5BA4A4] font-mono">Loading...</p>
                </div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/portal?mode=register" replace />;
    }

    // Recovery-only accounts never see trading pages
    if (user?.portal === 'recovery' && !user?.is_admin) {
        return <Navigate to="/recovery/dashboard" replace />;
    }
    
    return children;
};

// Recovery Route Component (Recovery Wallet portal) - admins may enter too
const RecoveryRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#07090d] flex items-center justify-center">
                <div className="w-12 h-12 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }
    if (!isAuthenticated) {
        return <Navigate to="/recovery/login" replace />;
    }
    if (user && user.portal !== 'recovery' && !user.is_admin) {
        return <Navigate to="/dashboard" replace />;
    }
    return (
        <RecoveryLayout>
            <Suspense fallback={<PageLoader />}>{children}</Suspense>
        </RecoveryLayout>
    );
};

// Admin Route Component (secret access)
const AdminRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="text-[#5BA4A4] text-xl font-mono">Loading...</div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/register" replace />;
    }
    
    if (!user?.is_admin) {
        return <Navigate to="/dashboard" replace />;
    }
    
    return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="text-[#5BA4A4] text-xl font-mono">Loading...</div>
            </div>
        );
    }
    
    if (isAuthenticated) {
        return <Navigate to={user?.portal === 'recovery' ? '/recovery/dashboard' : '/dashboard'} replace />;
    }
    
    return children;
};

// Layout with Sidebar and Mobile Header
const DashboardLayout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { isAuthenticated, user, updateUser, token } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
    const closeSidebar = () => setSidebarOpen(false);

    // Re-fetch user on route change to keep freeze status fresh
    useEffect(() => {
        if (!isAuthenticated || !token) return;
        const controller = new AbortController();
        (async () => {
            try {
                const axios = (await import('axios')).default;
                const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                    timeout: 8000,
                });
                if (res?.data && updateUser) {
                    updateUser(res.data);
                }
            } catch (e) {
                // Silent failure - non-critical
            }
        })();
        return () => controller.abort();
    }, [location.pathname, isAuthenticated, token, updateUser]);

    // Initialize push notifications when user is authenticated
    useEffect(() => {
        const initPush = async () => {
            if (isAuthenticated) {
                try {
                    await pushService.init();
                    // Auto-subscribe if permission already granted
                    if (Notification.permission === 'granted') {
                        await pushService.subscribe();
                    }
                } catch (error) {
                    console.error('Failed to initialize push notifications:', error);
                }
            }
        };
        initPush();
    }, [isAuthenticated]);

    // Initialize WebSocket for real-time notifications
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (isAuthenticated && token) {
            // Connect to WebSocket
            wsService.connect(token);
            
            // Listen for bot trade notifications
            const unsubBotTrade = wsService.on('bot_trade', (data) => {
                const trade = data.trade;
                const isWin = trade.profit_loss >= 0;
                toast[isWin ? 'success' : 'error'](
                    `Bot Trade: ${trade.symbol} ${trade.trade_type.toUpperCase()}`,
                    {
                        description: `P&L: ${isWin ? '+' : ''}$${trade.profit_loss?.toFixed(2)} | Balance: $${trade.new_balance?.toLocaleString()}`,
                        duration: 5000
                    }
                );
            });
            
            // Listen for connection status
            const unsubConnected = wsService.on('connected', () => {
                console.log('Real-time notifications active');
            });
            
            return () => {
                unsubBotTrade();
                unsubConnected();
                wsService.disconnect();
            };
        }
    }, [isAuthenticated]);

    return (
        <div className="app-container">
            {/* Real-time Stats Bar - Fixed at top */}
            <RealtimeStatsBar />
            
            {/* Mobile Header - only visible on mobile */}
            <MobileHeader isOpen={sidebarOpen} onToggle={toggleSidebar} />
            
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div 
                    className="mobile-overlay md:hidden" 
                    onClick={closeSidebar}
                />
            )}
            
            {/* Sidebar */}
            <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
            
            {/* Main Content - Add top padding for stats bar */}
            <main className="main-content pb-20 md:pb-0 pt-12">
                {children}
            </main>

            {/* Frozen Overlay - shown on every page except /portfolio. Admins bypass it completely. */}
            {user?.frozen && !user?.is_admin && location.pathname !== '/portfolio' && (
                <FrozenOverlay
                    reason={user?.freeze_reason}
                    until={user?.freeze_until}
                    onViewPortfolio={() => navigate('/portfolio')}
                    onContactSupport={() => {
                        // Open support chat if available, else toast with contact info
                        const chatBtn = document.querySelector('[data-testid="support-chat-btn"]');
                        if (chatBtn) {
                            chatBtn.click();
                        } else {
                            toast.message('Contact Support', {
                                description: 'Email support@cyberneticstrade.com or use the chat icon.',
                            });
                        }
                    }}
                />
            )}

            {/* Quick Action FAB - Mobile Only */}
            <QuickActionButton />

            {/* Mobile Bottom Navigation */}
            <MobileBottomNav />

            {/* Support Chat */}
            <SupportChat />
        </div>
    );
};

function AppContent() {
    return (
        <BrowserRouter>
            <div className="min-h-screen bg-[#050505]">
                <div className="noise-overlay" />
                <Routes>
                    {/* Public Routes */}
                    <Route 
                        path="/login" 
                        element={
                            <PublicRoute>
                                <LoginPage />
                            </PublicRoute>
                        } 
                    />
                    <Route 
                        path="/register" 
                        element={
                            <PublicRoute>
                                <RegisterPage />
                            </PublicRoute>
                        } 
                    />
                    
                    {/* Protected Routes with Suspense */}
                    <Route 
                        path="/dashboard" 
                        element={
                            <ProtectedRoute>
                                <DashboardLayout>
                                    <Suspense fallback={<PageLoader />}>
                                        <DashboardPage />
                                    </Suspense>
                                </DashboardLayout>
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/portfolio" 
                        element={
                            <ProtectedRoute>
                                <DashboardLayout>
                                    <Suspense fallback={<PageLoader />}>
                                        <PortfolioPage />
                                    </Suspense>
                                </DashboardLayout>
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/partners" 
                        element={
                            <ProtectedRoute>
                                <DashboardLayout>
                                    <Suspense fallback={<PageLoader />}>
                                        <PartnersPage />
                                    </Suspense>
                                </DashboardLayout>
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/deposit" 
                        element={
                            <ProtectedRoute>
                                <DashboardLayout>
                                    <Suspense fallback={<PageLoader />}>
                                        <DepositPage />
                                    </Suspense>
                                </DashboardLayout>
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/withdraw" 
                        element={
                            <ProtectedRoute>
                                <DashboardLayout>
                                    <Suspense fallback={<PageLoader />}>
                                        <WithdrawPage />
                                    </Suspense>
                                </DashboardLayout>
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/profile" 
                        element={
                            <ProtectedRoute>
                                <DashboardLayout>
                                    <Suspense fallback={<PageLoader />}>
                                        <ProfilePage />
                                    </Suspense>
                                </DashboardLayout>
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/coin/:coinId" 
                        element={
                            <ProtectedRoute>
                                <DashboardLayout>
                                    <Suspense fallback={<PageLoader />}>
                                        <CoinDetailPage />
                                    </Suspense>
                                </DashboardLayout>
                            </ProtectedRoute>
                        } 
                    />
                    
                    {/* Secret Admin Route - not visible in sidebar */}
                    <Route 
                        path="/admin-panel-secret" 
                        element={
                            <AdminRoute>
                                <DashboardLayout>
                                    <Suspense fallback={<PageLoader />}>
                                        <AdminPage />
                                    </Suspense>
                                </DashboardLayout>
                            </AdminRoute>
                        } 
                    />
                    
                    {/* Admin Console - Separate Admin Panel */}
                    <Route 
                        path="/console" 
                        element={
                            <AdminRoute>
                                <Suspense fallback={<PageLoader />}>
                                    <AdminConsolePage />
                                </Suspense>
                            </AdminRoute>
                        } 
                    />
                    
                    {/* News Feed */}
                    <Route 
                        path="/news" 
                        element={
                            <ProtectedRoute>
                                <DashboardLayout>
                                    <Suspense fallback={<PageLoader />}>
                                        <NewsFeedPage />
                                    </Suspense>
                                </DashboardLayout>
                            </ProtectedRoute>
                        } 
                    />

                    {/* Trading Bot */}
                    <Route
                        path="/trading-bot"
                        element={
                            <ProtectedRoute>
                                <DashboardLayout>
                                    <Suspense fallback={<PageLoader />}>
                                        <TradingBotPage />
                                    </Suspense>
                                </DashboardLayout>
                            </ProtectedRoute>
                        }
                    />

                    {/* Recovery Wallet Portal */}
                    <Route path="/portal" element={<PublicRoute><PortalChooserPage /></PublicRoute>} />
                    <Route path="/recovery/login" element={<PublicRoute><Suspense fallback={<PageLoader />}><RecoveryLoginPage /></Suspense></PublicRoute>} />
                    <Route path="/recovery/register" element={<PublicRoute><Suspense fallback={<PageLoader />}><RecoveryRegisterPage /></Suspense></PublicRoute>} />
                    <Route path="/recovery" element={<Navigate to="/recovery/dashboard" replace />} />
                    <Route path="/recovery/dashboard" element={<RecoveryRoute><RecoveryDashboardPage /></RecoveryRoute>} />
                    <Route path="/recovery/cases" element={<RecoveryRoute><RecoveryCasesPage /></RecoveryRoute>} />
                    <Route path="/recovery/deposit" element={<RecoveryRoute><RecoveryDepositPage /></RecoveryRoute>} />
                    <Route path="/recovery/methods" element={<RecoveryRoute><RecoveryMethodsPage /></RecoveryRoute>} />
                    <Route path="/recovery/documents" element={<RecoveryRoute><RecoveryDocumentsPage /></RecoveryRoute>} />
                    <Route path="/recovery/profile" element={<RecoveryRoute><RecoveryProfilePage /></RecoveryRoute>} />
                    <Route path="/recovery/support" element={<RecoveryRoute><RecoverySupportPage /></RecoveryRoute>} />
                    
                    {/* Article Pages for Press/Media */}
                    <Route 
                        path="/press/:publication" 
                        element={<ArticlePage />} 
                    />
                    
                    {/* Landing Page - Default for unauthenticated users */}
                    <Route 
                        path="/" 
                        element={
                            <PublicRoute>
                                <LandingPage />
                            </PublicRoute>
                        } 
                    />
                    
                    {/* Fallback redirects */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                
                <Toaster 
                    position="bottom-right"
                    toastOptions={{
                        style: {
                            background: '#0A0A0A',
                            border: '1px solid rgba(91, 164, 164, 0.3)',
                            color: '#fff',
                            fontFamily: 'Manrope, sans-serif',
                        },
                    }}
                />
            </div>
        </BrowserRouter>
    );
}

function App() {
    return (
        <AuthProvider>
            <RecoveryPortalProvider>
                <LiveTradingProvider>
                    <AppContent />
                </LiveTradingProvider>
            </RecoveryPortalProvider>
        </AuthProvider>
    );
}

export default App;
